import type { OrderBy } from '@/lib/graphql-query-builder';

/**
 * Scope note: survey forms use the same question engine as
 * hr-form-creation's review forms (confirmed: Angular's survey builder
 * embeds the identical ReviewFormBuilderComponent), but with no
 * audience-targeting fields (no AssessmentType/ReviewType/Designations) --
 * a survey form is just a title/description + question set.
 *
 * The real SurveyForm schema (confirmed against the .NET backend's
 * Survey.Domain models) supports sectioned forms via `HasSections` +
 * `SectionsJson` -- a sectioned form cannot be represented by
 * `QuestionsJson` alone. This module's editor UI is scoped down to
 * authoring flat (unsectioned) forms only (new forms are always created
 * with `HasSections: false`), but the data layer (types + GraphQL
 * selections) fully models sections so other surfaces (e.g. the survey
 * answer/response views) can read a sectioned form without data loss.
 * Full section-authoring UI is a follow-up.
 */
export type QuestionFormatType =
  | 'short-text'
  | 'long-text'
  | 'multiple-choice'
  | 'checkboxes'
  | 'rating'
  | 'date'
  | 'likert-scale'
  | 'nps';

export const QUESTION_FORMATS: { key: QuestionFormatType; title: string }[] = [
  { key: 'short-text', title: 'SHORT_TEXT' },
  { key: 'long-text', title: 'LONG_TEXT' },
  { key: 'multiple-choice', title: 'MULTIPLE_CHOICE' },
  { key: 'checkboxes', title: 'CHECKBOXES' },
  { key: 'rating', title: 'RATING' },
  { key: 'date', title: 'DATE' },
  { key: 'likert-scale', title: 'LIKERT_SCALE' },
  { key: 'nps', title: 'NPS' },
];

interface QuestionOption {
  Label: string;
  Value: string;
}

export interface Question {
  QuestionId: string;
  FormatType: QuestionFormatType;
  Question: string;
  QuestionDescription?: string;
  Position: number;
  IsRequired: boolean;
  HasComment: boolean;
  Options?: QuestionOption[];
  MinRating?: number;
  MaxRating?: number;
  MinRatingLabel?: string;
  MaxRatingLabel?: string;
  LikertLabels?: string[];
  LikertStatements?: string[];
}

export interface SurveyForm {
  ItemId: string;
  Title: string;
  Description?: string;
  HasSections: boolean;
  QuestionsJson: string;
  SectionsJson?: string;
  CreatedDate?: string;
  LastUpdatedDate?: string;
}

export interface SurveyFormsPagingResult {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  totalCount: number;
  totalPages: number;
  pageSize: number;
  pageNo: number;
  items: SurveyForm[];
}

export interface GetSurveyFormsParams {
  pageNo: number;
  pageSize: number;
  filter?: Record<string, unknown>;
  /** Defaults to latest change first. */
  order?: OrderBy[];
}

export interface SurveyFormInsertInput {
  Title: string;
  Description?: string;
  QuestionsJson: string;
  HasSections?: boolean;
  SectionsJson?: string;
}

export type SurveyFormUpdateInput = Partial<SurveyFormInsertInput>;
